import { createContext, memo, type ReactNode, type RefObject, useContext, useMemo } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type ScrollElementContextType = {
  scrollElementRef: RefObject<HTMLDivElement | HTMLElement | null>;
};

const defaultValue: ScrollElementContextType = {
  scrollElementRef: { current: null },
};

export const ScrollElementContext = createContext(defaultValue);

export type ScrollElementProviderProps = {
  scrollElementRef: RefObject<HTMLDivElement | HTMLElement | null>;
  children: ReactNode;
};

/**
 * @deprecated use ScrollElementContextProvider instead
 */
export const ScrollElementProvider = memo(({ scrollElementRef, children }: ScrollElementProviderProps) => {
  const value = useMemo(() => ({ scrollElementRef }), [scrollElementRef]);

  return <ScrollElementContext.Provider value={value}>{children}</ScrollElementContext.Provider>;
});

ScrollElementProvider.displayName = 'ScrollElementProvider';

export const useScrollElementContext = () => {
  const context = useContext(ScrollElementContext);

  if (context === defaultValue) {
    const error = 'ScrollElementContext used outside of ScrollElementProvider';

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
